import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowLeft, ShieldCheck, Info } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Checkout() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get("plan");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  const plan = useMemo(() => plans.find((p) => p.id === planId), [plans, planId]);

  useEffect(() => {
    if (plans.length && !plan) nav("/pricing", { replace: true });
  }, [plans, plan, nav]);

  if (!plan) return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-20 text-center text-stone-500">…</div>;

  const nameOf = (p) => (lang === "fr" ? p.name_fr : p.name_en);
  const tagOf = (p) => (lang === "fr" ? p.tagline_fr : p.tagline_en);
  const featsOf = (p) => (lang === "fr" ? p.features_fr : p.features_en);
  const fmtPrice = (v) => (v % 1 === 0 ? String(v) : v.toFixed(2).replace(".", lang === "fr" ? "," : "."));
  const priceLabel = plan.price === 0 ? (lang === "fr" ? "0€" : "$0") : (lang === "fr" ? `${fmtPrice(plan.price)}€` : `$${fmtPrice(plan.price)}`);
  const isFree = plan.price === 0;

  const onConfirm = () => {
    if (!user) return nav(`/auth?mode=signup&plan=${plan.id}`);
    return nav("/earn");
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <button onClick={() => nav("/pricing")} data-testid="checkout-back" className="inline-flex items-center gap-2 text-stone-500 hover:text-primary font-semibold mb-6">
        <ArrowLeft size={18} /> {t("checkout_back")}
      </button>
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-8" data-testid="checkout-title">{t("checkout_title")}</h1>

      <div className="grid md:grid-cols-5 gap-6 items-start">
        {/* Selected plan */}
        <div className="md:col-span-3 rounded-3xl border-2 border-primary/60 bg-white p-8 shadow-xl" data-testid="checkout-plan">
          <div className="text-xs font-black uppercase tracking-widest text-primary mb-2">{t("checkout_selected")}</div>
          <h2 className="font-head font-black text-2xl" data-testid="checkout-plan-name">{nameOf(plan)}</h2>
          <p className="text-stone-500 mb-4">{tagOf(plan)}</p>
          <div className="mb-6">
            <span className="font-head font-black text-4xl">{priceLabel}</span>
            {plan.price > 0 && <span className="text-stone-500">{t("pricing_month")}</span>}
          </div>
          <ul className="space-y-3">
            {featsOf(plan).map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0"><Check size={13} /></span>
                <span className="text-stone-700">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Summary / confirm */}
        <div className="md:col-span-2 rounded-3xl border-2 border-stone-200 bg-white p-8">
          <h3 className="font-head font-black text-xl mb-4">{t("checkout_summary")}</h3>
          <div className="flex items-center justify-between mb-2">
            <span className="text-stone-600">{nameOf(plan)}</span>
            <span className="font-bold">{priceLabel}{plan.price > 0 ? t("pricing_month") : ""}</span>
          </div>
          <div className="flex items-center justify-between border-t border-stone-100 pt-3 mb-6">
            <span className="font-bold">{t("checkout_total")}</span>
            <span className="font-head font-black text-xl">{priceLabel}{plan.price > 0 ? t("pricing_month") : ""}</span>
          </div>

          <div className="flex gap-2 rounded-2xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-600 mb-5" data-testid="checkout-notice">
            <Info size={16} className="shrink-0 mt-0.5 text-primary" />
            <span>{isFree ? t("checkout_free_note") : t("checkout_notice")}</span>
          </div>

          <Button onClick={onConfirm} data-testid="checkout-confirm"
            className="w-full rounded-full min-h-[54px] font-bold text-base bt-gradient text-white hover:opacity-90">
            {user ? t("checkout_confirm_user") : t("checkout_confirm")}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-stone-400 mt-4"><ShieldCheck size={13} /> BETOOL</p>
        </div>
      </div>
    </div>
  );
}
